// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { consoleService } from '../../services/consoleService';
import { Leaderboard, LeaderboardRecord } from '../../types/leaderboards';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';
import './LudoLeaderboard.scss';

interface LudoLeaderboardProps {
  leaderboardId?: string;
}

const LudoLeaderboard: React.FC<LudoLeaderboardProps> = ({ 
  leaderboardId = 'total_earned_coins_ludo' 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [leaderboard, setLeaderboard] = useState<Leaderboard | null>(null);
  const [records, setRecords] = useState<LeaderboardRecord[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();

  const loadLeaderboardData = async () => {
    try {
      setError(null);
      const data = await consoleService.getLudoLeaderboard(leaderboardId);
      setLeaderboard(data.leaderboard);
      setRecords(data.records.records || []);
    } catch (err) {
      console.error('Failed to load leaderboard data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load leaderboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadLeaderboardData();
  }, [leaderboardId]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadLeaderboardData();
  };

  const formatScore = (score: string): string => {
    const numScore = parseInt(score) || 0;
    return numScore.toLocaleString();
  };

  const getRankIcon = (rank: number): string => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '🏅';
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDeleteRecord = (record: LeaderboardRecord, index: number) => {
    openConfirmDialog(
      'Remove Record',
      `Are you sure you want to remove the record for "${record.username || record.owner_id}"? This action cannot be undone.`,
      async () => {
        try {
          await consoleService.deleteLeaderboardRecord('', leaderboardId, record.owner_id);
          setRecords(prev => prev.filter((_, i) => i !== index));
          setError('Record removed successfully!');
          setTimeout(() => setError(null), 3000);
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to remove record');
        }
      }
    );
  };

  const handleResetLeaderboard = () => {
    openConfirmDialog(
      'Reset Leaderboard',
      `Are you sure you want to reset the leaderboard "${leaderboardId}"? This will delete all records and cannot be undone.`,
      async () => {
        setResetting(true);
        setError(null);
        try {
          await consoleService.leaderboardReset(leaderboardId);
          await loadLeaderboardData();
          setError('Leaderboard reset successfully!');
          setTimeout(() => setError(null), 3000);
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to reset leaderboard');
        } finally {
          setResetting(false);
        }
      }
    );
  };

  if (loading) {
    return (
      <div className="ludo-leaderboard">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <span>Loading leaderboard...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ludo-leaderboard">
        <div className="error-container">
          <div className="error-icon">❌</div>
          <h3>Failed to Load Leaderboard</h3>
          <p>{error}</p>
          <button onClick={handleRefresh} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ludo-leaderboard">
      {/* Leaderboard Header */}
      <div className="leaderboard-header-actions">
        <button
          type="button"
          className="btn btn-danger"
          onClick={handleResetLeaderboard}
          disabled={resetting}
        >
          {resetting ? 'Resetting...' : '🔄 Reset Leaderboard'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing...' : '🔄 Refresh'}
        </button>
      </div>

      {error && (
        <div className={`alert ${error.includes('successfully') ? 'alert-success' : 'alert-danger'}`}>
          {error}
        </div>
      )}



      {/* Leaderboard Table */}
      <div className="leaderboard-table-container">
        {records.length > 0 ? (
          <div className="leaderboard-table">
            <div className="table-header">
              <div className="header-cell rank-cell">Rank</div>
              <div className="header-cell player-cell">Player</div>
              <div className="header-cell score-cell">Score</div>
              <div className="header-cell date-cell">Last Updated</div>
              <div className="header-cell actions-cell">Actions</div>
            </div>
            <div className="table-body">
              {records.map((record, index) => {
                const rank = record.rank || (index + 1);
                return (
                  <div key={record.owner_id} className={`table-row ${rank <= 3 ? 'top-rank' : ''}`}>
                    <div className="table-cell rank-cell">
                      <span className="rank-icon">{getRankIcon(rank)}</span>
                      <span className="rank-number">#{rank}</span>
                    </div>
                    <div className="table-cell player-cell">
                      <div className="player-info">
                        <div className="player-name">
                          {record.username || 'Anonymous Player'}
                        </div>
                        <div className="player-id">
                          ID: {record.owner_id.substring(0, 8)}...
                        </div>
                      </div>
                    </div>
                    <div className="table-cell score-cell">
                      <div className="score-value">
                        {formatScore(record.score)}
                      </div>
                    </div>
                    <div className="table-cell date-cell">
                      <div className="update-time">
                        {formatDate(record.update_time)}
                      </div>
                    </div>
                    <div className="table-cell actions-cell">
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteRecord(record, index)}
                        title="Remove record"
                      >
                        🗑️ Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="empty-leaderboard">
            <div className="empty-icon">🏆</div>
            <h3>No Rankings Yet</h3>
            <p>Be the first to earn coins and claim the top spot!</p>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
};

export default LudoLeaderboard;
